import React, { useState, useCallback, useEffect, useRef } from 'react';
import {Spin, message } from 'antd';
import { getTemplateList, deleteTemplate, updateTemplate } from '@model/template';
import TemplateEditorDialog from './TemplateEditorDialog';
import {getImagePathWithTheme, getImagePathWithThemeSvg, isDarkMode} from "@/utils";
import { t } from 'i18next';
import {getTiptapExtension} from "@/components/EditorV2/TiptapTiptapExtension";
import {EditorContent, useEditor } from '@tiptap/react';
import {KEYS} from "../../../@excalidraw/packages/excalidraw/keys";
import {createPortal} from "react-dom";
import './../EditorV2/SmallEditStyle.css'

interface TemplateSelectorState {
  isOpen: boolean;
}

interface TemplateItem {
  id: string;
  name: string;
  content: string;
  createTime: number;
  updateTime: number;
}

class TemplateSelectorManager {
  private static instance: TemplateSelectorManager;
  private setState: React.Dispatch<React.SetStateAction<TemplateSelectorState>> | null = null;
  private resolvePromise: ((value: TemplateItem | null) => void) | null = null;

  static getInstance(): TemplateSelectorManager {
    if (!TemplateSelectorManager.instance) {
      TemplateSelectorManager.instance = new TemplateSelectorManager();
    }
    return TemplateSelectorManager.instance;
  }

  init(setState: React.Dispatch<React.SetStateAction<TemplateSelectorState>>) {
    this.setState = setState;
  }

  /**
   * 打开模板选择弹窗
   */
  open(): Promise<TemplateItem | null> {
    return new Promise((resolve) => {
      if (!this.setState) {
        resolve(null);
        return;
      }

      this.resolvePromise = resolve;

      this.setState({
        isOpen: true,
      });
    });
  }

  close(result: TemplateItem | null = null) {
    if (this.resolvePromise) {
      this.resolvePromise(result);
      this.resolvePromise = null;
    }
    if (this.setState) {
      this.setState({ isOpen: false });
    }
  }
}

// 模板列表项组件
const TemplateListItem: React.FC<{
  template: TemplateItem;
  isMenuOpen: boolean;
  onMenuToggle: (templateId: string | null) => void;
  onSelect: (template: TemplateItem) => void;
  onEdit: (template: TemplateItem) => void;
  onDelete: (template: TemplateItem) => void;
  onHover:(template:TemplateItem)=>void;
}> = ({ template, isMenuOpen, onMenuToggle, onSelect, onEdit, onDelete ,onHover}) => {
  const [isHovered, setIsHovered] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // 检查点击是否在菜单或更多按钮内
      if (
        menuRef.current && 
        !menuRef.current.contains(e.target as Node) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(e.target as Node)
      ) {
        onMenuToggle(null);
      }
    };
    if (isMenuOpen) {
      // 使用 click 事件而不是 mousedown，避免与 more icon 的点击冲突
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen, onMenuToggle]);

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isMenuOpen) {
      // 如果菜单已经打开，则关闭
      onMenuToggle(null);
    } else {
      // 打开当前菜单（会自动关闭其他菜单）
      onMenuToggle(template.id);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 12px',
        height:"40px",
        cursor: 'pointer',
        backgroundColor: (template.isHover) ? 'var(--hoverd-template-background)' : 'transparent',
        borderRadius: '8px',
        position: 'relative',
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={() => {
          setIsHovered(true);
          onHover(template);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      onClick={() => onSelect(template)}
    >
      {/* 模板图标 */}
      <div style={{ marginRight: '6px', display: 'flex', alignItems: 'center' }}>
        <img className={'img-26'} src={getImagePathWithThemeSvg('note_template_item')}/>
      </div>

      {/* 模板名称 */}
      <div
        style={{
          flex: 1,
          fontSize: '14px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {template.name}
      </div>

      {/* 更多按钮 */}
      {(isHovered || isMenuOpen) && (
        <div
          ref={moreButtonRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '4px',
            cursor: 'pointer',
            // backgroundColor: isMenuOpen ? '#e8e8e8' : 'transparent',
          }}
          onClick={handleMoreClick}
          onMouseEnter={(e) => {
            // e.currentTarget.style.backgroundColor = '#e8e8e8';
          }}
          onMouseLeave={(e) => {
            // if (!isMenuOpen) {
            //   e.currentTarget.style.backgroundColor = 'transparent';
            // }
          }}
        >
          <img className={'img-18'} src={getImagePathWithThemeSvg('note_template_item_more')}/>
        </div>
      )}

      {/* 下拉菜单 */}
      {isMenuOpen && (
        <div
          ref={(el) => {
            (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
            if (el && moreButtonRef.current) {
              const buttonRect = moreButtonRef.current.getBoundingClientRect();
              el.style.left = `${buttonRect.right - 26}px`;// - el.offsetWidth
              el.style.top = `${buttonRect.bottom + 4}px`;
            }
          }}
          style={{
            position: 'fixed',
            borderRadius: '8px',
              boxShadow: 'var(--dialog-box-shadow)',
            zIndex: 10010,
            minWidth: '164px',
            padding: '4px 0px',
              background:'var(--dialog-dialog-background-color)'
          }}
          className={'default-font-family default-font-size shadow'}
          onClick={(e) => e.stopPropagation()}
        >
          <div
              className={'dropdown-item'}
            style={{
              fontSize: '12px',
              cursor: 'pointer',
                padding:'0 10px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--button-background-color)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle(null);
              onEdit(template);
            }}
          >
              <span>{t("Edit")}</span>
          </div>
          <div
              className={'dropdown-item'}
            style={{
              fontSize: '12px',
              cursor: 'pointer',
                padding:'0 10px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--button-background-color)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle(null);
              onDelete(template);
            }}
          >
              <span>{t("Delete")}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// 删除确认弹窗组件
const DeleteConfirmDialog: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          width: '480px',
          padding: '24px',
          boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
          Delete template
        </div>
        <div style={{ fontSize: '14px', color: '#333', marginBottom: '32px' }}>
          确认删除模板？此操作不可撤销
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
          <button
            onClick={onCancel}
            style={{
              minWidth: '100px',
              height: '36px',
              padding: '0 24px',
              fontSize: '14px',
              fontWeight: 400,
              color: '#000',
              backgroundColor: '#fff',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#4096ff';
              e.currentTarget.style.color = '#4096ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d9d9d9';
              e.currentTarget.style.color = '#000';
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              minWidth: '100px',
              height: '36px',
              padding: '0 24px',
              fontSize: '14px',
              fontWeight: 400,
              color: '#fff',
              backgroundColor: '#4080ff',
              border: '1px solid #4080ff',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#5a94ff';
              e.currentTarget.style.borderColor = '#5a94ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#4080ff';
              e.currentTarget.style.borderColor = '#4080ff';
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// 选择弹窗组件
const TemplateSelectorDialog: React.FC<{
  onSelect: (template: TemplateItem) => void;
  onClose: () => void;
}> = ({ onSelect, onClose }) => {
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<TemplateItem | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<TemplateItem | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [isSearching,setIsSearching] = useState(false);
  const inputRef = useRef(null);
  const [inputSearchValue,setInputSearchValue] = useState('');
  const mSearchKey = useRef('');
  const [selectData,setSelectData] = useState(null);
  const divListContainerRef = useRef(null);
  const mArrayTemplates = useRef<any>(null);

    useEffect(()=>{
        document.body.addEventListener('keydown',onKeydown);

        return ()=>{
            document.body.removeEventListener('keydown',onKeydown)
        }
    })

  const handleMenuToggle = useCallback((templateId: string | null) => {
    setActiveMenuId(templateId);
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const list = await getTemplateList();
      // 按创建时间降序排序（最新的在最上面）
      let sorted = list.sort((a: TemplateItem, b: TemplateItem) => b.createTime - a.createTime);
      if (mSearchKey.current){
          sorted = sorted.filter(p=>p.name?.toLowerCase()?.includes(mSearchKey.current?.toLowerCase()));
      }
      sorted.forEach(p=>{
          delete p.isHover;
      })
      if (sorted.length > 0){
          sorted[0].isHover = true;
      }
        mArrayTemplates.current = sorted;
      setTemplates(sorted);
      setIsSearching(false);
        if (sorted.length > 0){
            showPreview(sorted[0])
        }else{
            showPreview(null);
        }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

    useEffect(() => {
        setTimeout(()=>{
            inputRef.current?.focus();
        },200)
    }, []);

    function onKeydown(event:any){
        if (event.code == KEYS.ARROW_DOWN){
            var nIndex = templates.findIndex(p=>p.isHover);
            var arrayNew = [...templates];
            arrayNew.forEach(p=>{
                p.isHover = false;
            })
            if (nIndex >= templates.length-1){
                nIndex = 0;
            }else{
                nIndex++;
            }
            arrayNew[nIndex].isHover = true;
            setTemplates(arrayNew);

            //显示这个的预览
            showPreview(arrayNew[nIndex]).then(res=>{
                scrollToIndex(nIndex);
            });
            event.preventDefault();
            event.stopPropagation();
        }else if (event.code == KEYS.ARROW_UP){
            var nIndex = templates.findIndex(p=>p.isHover);
            var arrayNew = [...templates];
            arrayNew.forEach(p=>{
                p.isHover = false;
            })
            if (nIndex == 0){
                nIndex = templates.length -1;
            }else{
                nIndex--;
            }
            arrayNew[nIndex].isHover = true;
            setTemplates(arrayNew);

            //显示这个的预览
            showPreview(arrayNew[nIndex]).then(res=>{
                scrollToIndex(nIndex);
            });
            event.preventDefault();
            event.stopPropagation();
        }else if (event.code == KEYS.ENTER){
            //选择这个...
            const nIndex = templates.findIndex(p=>p.isHover);

            event.preventDefault();
            event.stopPropagation();
        }
    }

  const handleEdit = (template: TemplateItem) => {
    setEditingTemplate(template);
  };

  const handleDelete = (template: TemplateItem) => {
      window.showMessageBox(t("Delete template"), t("ConfirmDeleteTemplate"), t("Cancel"), t("Delete"), null, async () => {
          try {
              await deleteTemplate(template.id);
              setDeletingTemplate(null);
              loadTemplates();
          } catch (error) {
              message.error('Failed to delete template');
          }
      })
  };


  const handleEditorClose = () => {
    setEditingTemplate(null);
    //刷新右侧预览...

  };

  const handleEditorDeleted = () => {
    setEditingTemplate(null);
    loadTemplates();
  };

  const handleEditorUpdated = (updated: TemplateItem) => {
    // 更新本地列表中的模板
    setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
    //更新预览...
      showPreview(updated);
  };

  const handleHover = (template)=>{
      showPreview(template);
      const arrayTemplates = [...templates];
      arrayTemplates.forEach(p=>p.isHover = false);
      template.isHover = true;
      setTemplates(arrayTemplates);
      if (activeMenuId != template.id){
          setActiveMenuId(null);
      }
  }


    let editor = useEditor({
        autofocus:false,
        injectCSS: true,
        editable:false,
        extensions: getTiptapExtension(null,null),
        editorProps: {
            attributes: {
                // class: '',
            },
        },
        onCreate(options){
            const nIndex = mArrayTemplates.current?.findIndex(p=>p.isHover);
            if (nIndex >= 0){
                const item = (mArrayTemplates.current[nIndex]);
                showPreview(item);
            }
            updateEditDefaultSize();
            options.editor.commands.setFontSize('12px');

        },
        parseOptions: {
            preserveWhitespace: 'full',
        },
    });

    /**
     * 滚动到索引
     * @param nIndex
     */
    const scrollToIndex = (nIndex:number)=>{
        if (divListContainerRef.current){
            if (nIndex > 3){
                divListContainerRef.current?.scrollTo(0,(nIndex-3)*40 + 16)
            }else{
                divListContainerRef.current?.scrollTo(0,0)
            }
        }
    }

    //显示预览
    async function showPreview(item:any){
        updateEditDefaultSize();
        setSelectData(item);
        if (item){
            editor?.commands.setContent(item.content);
        }else{
            editor?.commands.setContent('');
        }
    }

    /**
     * 重置预览
     */
    const resetPreview = ()=>{
        setSelectData(null);
        editor?.commands.setContent('');
    }

    const updateEditDefaultSize = ()=>{
        setTimeout(()=>{
            const div = document.getElementById('editor-small-preview');
            if (div){
                const editDiv = div.childNodes[0];
                editDiv?.classList.remove('ProseMirror');
                editDiv.classList.add('ProseMirror-card-small')
            }
        },100)
    }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
        }}
        onClick={onClose}
      >
        <div
            id={'div-template-dialog'}
          style={{
            backgroundColor: 'var(--background-color-white)',
            borderRadius: '8px',
            width: '710px',
            height: '480px',
              maxHeight:'480px',
            display: 'flex',
              overflow:'hidden',
            flexDirection: 'column',
            boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
          }}
          onClick={(e) => {
              e.stopPropagation();
              setActiveMenuId(null);
          }}
        >
          {/* 头部 */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderBottom: '1px solid var(--border-color-e4)',
                }}>
                <div className='input-border-no-background flex-row-vertical-center'
                     style={{
                         margin: '0px -6px',
                         background: isDarkMode()?'#292C32':'var(--input-background-card-template)!important',
                         flexShrink: '0',
                         flexGrow: '1',
                         border:isDarkMode()?'none':'1px solid var(--input-border-color-5f5f5f);'
                     }}>
                    <input ref={inputRef} style={{
                        marginTop: '0px',
                        border: 'none',
                        outline: 'none',
                        flexGrow: '1',
                        paddingLeft: '8px',
                        background: 'transparent'
                    }} className='font-family-regular font-size-12'
                           onKeyDown={(event) => {
                               event.stopPropagation();
                           }}
                           onChange={(e)=>{
                               mSearchKey.current = e.currentTarget.value;
                                setInputSearchValue(e.currentTarget.value);
                                // setIsSearching(true);
                                loadTemplates();
                           }} placeholder={t("Search")} value={inputSearchValue}/>
                    {
                        isSearching &&
                        <Spin indicator={<img className={'img-16 rotating-image'}
                                              src={getImagePathWithTheme('indicator_black')} />} size={'small'}/>
                    }

                    {
                        !isSearching && inputSearchValue &&
                        <img className={'img-8 cursor-pointer'} src={getImagePathWithTheme('close_black')} onClick={(e)=>{
                            mSearchKey.current = '';
                            setInputSearchValue('');
                            inputRef.current?.focus();
                            setIsSearching(true);
                            loadTemplates();
                        }}/>
                    }

                </div>
            </div>

            {/* 列表区域 */}
            <div style={{display:'grid',gridTemplateColumns:'1.5fr 1fr',height:'426px'}}>
                <div style={{borderRight: '1px solid var(--border-color-e4)',paddingBottom:'12px',flex: 1,height:'426px'}}>
                    <div
                        ref={divListContainerRef}
                        style={{
                            overflowY: 'auto',
                            padding: '8px 8px',
                            height:'410px',
                        }}>
                        {templates.length === 0 ? (
                            <div
                                className={'flex-center'}
                                style={{
                                    padding: '40px 20px',
                                    textAlign: 'center',
                                    color: 'var(--text-no-content-tips)',
                                    fontSize: '14px',
                                    height:'100%',
                                }}
                            >
                                {t('LibraryIsEmptyTips')}
                            </div>
                        ) : (
                            templates.map((template) => (
                                <TemplateListItem
                                    key={template.id}
                                    template={template}
                                    isMenuOpen={activeMenuId === template.id}
                                    onMenuToggle={handleMenuToggle}
                                    onSelect={onSelect}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onHover={handleHover}
                                />
                            ))
                        )}
                    </div>
                </div>


                {/*预览*/}
                <div style={{overflow: 'hidden'}}>
                    <EditorContent id={'editor-small-preview'}
                                   className={'tableStyle'}
                                   style={{
                                       padding: '0px 12px 12px 12px',
                                       fontSize: '12px',
                                       marginTop: '12px',
                                       overflow: 'auto',
                                       height: '410px'
                                   }}
                                   editor={editor}/>
                </div>
            </div>

        </div>
      </div>

        {/* 编辑器弹窗 */}
        {editingTemplate && (
            <TemplateEditorDialog
                template={editingTemplate}
                onClose={handleEditorClose}
                onDeleted={handleEditorDeleted}
                onUpdated={handleEditorUpdated}
            />
        )}

    </>
  );
};

// Provider 组件
const TemplateSelectorProvider: React.FC = () => {
    const [state, setState] = useState<TemplateSelectorState>({
        isOpen: false,
    });


    useEffect(() => {
        const manager = TemplateSelectorManager.getInstance();
        manager.init(setState);
    }, []);

    const handleSelect = useCallback((template: TemplateItem) => {
        const manager = TemplateSelectorManager.getInstance();
        manager.close(template);
    }, []);

    const handleClose = useCallback(() => {
        const manager = TemplateSelectorManager.getInstance();
        manager.close(null);
    }, []);

    if (!state.isOpen) return null;


    return createPortal(
    <TemplateSelectorDialog
      onSelect={handleSelect}
      onClose={handleClose}
    />
  ,document.body);
};

// 导出单例实例和组件
export const TemplateSelector = TemplateSelectorManager.getInstance();
export default TemplateSelectorProvider;

